import { TEST_IDS } from '@va/constants';
import { TooltipWrapper, useTooltipContext } from '@va/ui/tooltips';
import classNames from 'classnames';
import dayjs, { Dayjs } from 'dayjs';
import { memo, RefObject, useCallback, useEffect, useRef } from 'react';
import { useDatePickerContext } from '../context';
import { CalendarDate, CalendarObj } from '../types';

type CalendarProps = {
  calendar: CalendarObj;
};

const COLUMN_COUNT = 7;

export const Calendar = memo(({ calendar }: CalendarProps) => {
  const tooltip = useTooltipContext();
  const { getButtonClasses, getDayClasses } = useCalendarClasses();
  const { tooltipContent, isBetweenRange, setDate } = useDatePickerContext();

  const containerRef = useRef<HTMLDivElement>(null);

  useCalendarGridNavigation(containerRef, [calendar]);

  const hideDatePicker = useCallback(() => tooltip.context.onOpenChange(false), [tooltip]);

  return (
    <div
      ref={containerRef}
      tabIndex={0}
      className='grid grid-cols-7 gap-y-0.5 my-1'
      data-testid={TEST_IDS.generic.datePicker.daysPanel}
    >
      {calendar.date().map((date, index) => {
        return (
          <TooltipWrapper
            interactive
            disabled={!date.disabled || date.unix() > dayjs().unix()}
            content={tooltipContent}
            key={date.toISOString()}
          >
            <div
              className={classNames(
                'relative ',
                {
                  'text-black bg-class': isBetweenRange(date),
                },
                getDayClasses(date),
              )}
            >
              <button
                data-index={index}
                tabIndex={-1}
                type='button'
                className={classNames(
                  `calendar-date-btn z-1 relative w-12 h-12 flex justify-center items-center text-xs 2xl:text-sm focus:outline-hidden transition-all`,
                  {
                    'calendar-date-btn-hover': !date.disabled,
                  },
                  getButtonClasses(date),
                )}
                disabled={date.disabled}
                onClick={(e) => {
                  e.preventDefault();
                  setDate(date, hideDatePicker);
                }}
                data-testid={TEST_IDS.generic.datePicker.dayOption(date.date())}
              >
                {date.date()}
              </button>
            </div>
          </TooltipWrapper>
        );
      })}
    </div>
  );
});

const useCalendarClasses = () => {
  const {
    autoApply,
    value,
    state: { pendingValue, previous },
  } = useDatePickerContext();

  const getButtonClasses = useCallback(
    (date: CalendarDate) => {
      const { today, active, off, disabled } = date;

      let classes: string = '',
        startDate: Dayjs | undefined = undefined,
        endDate: Dayjs | undefined = undefined;

      if (!previous && autoApply && value) {
        [startDate, endDate] = value;
      } else if (pendingValue.length) {
        [startDate, endDate] = previous && autoApply ? [pendingValue[0]] : pendingValue;
      }

      if (active) {
        classes = classNames({
          'text-primary-500 font-semibold dark:text-primary-400 rounded-full': today,
          'text-primary-600 font-normal disabled:text-primary-500 disabled:cursor-not-allowed rounded-full': disabled,
          'text-primary-700 font-semibold dark:text-primary-100 rounded-full': date.isBetween(
            startDate,
            endDate,
            'date',
            '()',
          ),
          'text-primary-600 font-semibold dark:text-primary-200 rounded-full':
            !today && !disabled && !date.isBetween(startDate, endDate, 'date', '()'),
        });
      }

      if (off) {
        classes = classNames(`text-primary-400 font-light disabled:cursor-not-allowed rounded-full`);
      }

      if (startDate && endDate && !off && (date.isSame(startDate, 'date') || date.isSame(endDate, 'date'))) {
        classes = classNames('bg-primary text-white font-bold disabled:cursor-not-allowed', {
          'rounded-l-full': date.isSame(startDate, 'date'),
          'rounded-r-full': date.isSame(endDate, 'date'),
          'rounded-full': date.isSame(endDate, 'date') && date.isSame(startDate, 'date'),
        });
      } else if (startDate && date.isSame(startDate, 'date') && !off) {
        classes = classNames('bg-primary text-white font-bold rounded-full disabled:cursor-not-allowed');
      }

      return classes;
    },
    [previous, autoApply, value, pendingValue],
  );

  const getDayClasses = useCallback(
    (date: Dayjs) => {
      let startDate, endDate;

      if (autoApply && value) {
        [startDate, endDate] = value;
      } else if (pendingValue.length) {
        [startDate, endDate] = pendingValue;
      }

      return classNames('inset-0', {
        'rounded-l-full': date.isSame(startDate, 'date'),
        'rounded-r-full': date.isSame(endDate, 'date'),
      });
    },
    [autoApply, pendingValue, value],
  );

  return { getButtonClasses, getDayClasses };
};

const useCalendarGridNavigation = (containerRef: RefObject<HTMLDivElement | null>, dependencies: any[] = []) => {
  const focusedButtonIndex = useRef<number>(-1);
  const focusedButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const containerEl = containerRef.current;

    const daysButtons = containerEl.querySelectorAll('button');

    if (document.activeElement === containerEl && !containerEl.contains(focusedButtonRef.current)) {
      const index = focusedButtonIndex.current === -1 ? 0 : focusedButtonIndex.current;
      focusButton(daysButtons[index] ?? null);
    }

    function onFocus() {
      if (!focusedButtonRef.current) focusButton(daysButtons[0] ?? null);
    }

    function onBlur() {
      defocusButton(focusedButtonRef.current);
    }

    function focusButton(buttonEl: HTMLButtonElement | null) {
      focusedButtonRef.current = buttonEl;

      if (containerEl.matches(':focus-visible')) focusedButtonRef.current?.classList.add('focused');
    }

    function defocusButton(buttonEl: HTMLButtonElement | null) {
      focusedButtonRef.current = null;
      buttonEl?.classList.remove('focused');
    }

    function handleKeyDown(event: KeyboardEvent) {
      focusedButtonIndex.current = parseInt(focusedButtonRef.current?.['dataset'].index ?? '-1', 10);

      if (focusedButtonIndex.current === -1 || !daysButtons?.length) return;

      switch (event.key) {
        case 'ArrowDown': {
          const nextRow = daysButtons[focusedButtonIndex.current + COLUMN_COUNT];
          defocusButton(focusedButtonRef.current);
          focusButton(nextRow);
          break;
        }
        case 'ArrowUp': {
          const prevRow = daysButtons[focusedButtonIndex.current - COLUMN_COUNT];
          defocusButton(focusedButtonRef.current);
          focusButton(prevRow);
          break;
        }
        case 'ArrowLeft': {
          const prevDate = daysButtons[focusedButtonIndex.current - 1];
          defocusButton(focusedButtonRef.current);
          focusButton(prevDate);
          break;
        }
        case 'ArrowRight': {
          const nextDate = daysButtons[focusedButtonIndex.current + 1];
          defocusButton(focusedButtonRef.current);
          focusButton(nextDate);
          break;
        }
        case 'Enter':
        case ' ':
          if (focusedButtonIndex.current === -1 || !focusedButtonRef.current) return;

          focusedButtonRef.current.click();
          setTimeout(() => {
            const buttonElements = containerEl.querySelectorAll('button');
            focusedButtonRef.current = buttonElements[focusedButtonIndex.current];
            focusButton(focusedButtonRef.current);
          }, 0);
          break;
        default:
          break;
      }
    }

    containerEl.addEventListener('keydown', handleKeyDown);
    containerEl.addEventListener('focus', onFocus);
    containerEl.addEventListener('blur', onBlur);

    return () => {
      containerEl.removeEventListener('keydown', handleKeyDown);
      containerEl.removeEventListener('focus', onFocus);
      containerEl.removeEventListener('blur', onBlur);
    };
  }, [containerRef, ...dependencies]);
};
